import _, { groupBy, sortBy } from 'lodash';
import { useContext, useMemo } from 'react';
import { Feature } from 'geojson';
import styled from 'styled-components';
import { FieldsPreviewContext, SectionHeader } from './components';
import { PANEL_INNER_HEIGHT } from './constants';
import { PanelSection } from '../SidePanel';
import { MapContext } from '../../contexts/MapContext';
import { SOIL_BOUNDARIES_LAYER_ID } from '../../constants/map';
import { layerHasData } from '../../utils/layers';
import SoilIcon from '../icons/SoilIcon';
import { SquareSwatch } from '../right-menu/SquareSwatch';
import { darkenColor } from '../../utils/colors';
import { WrappedNameDiv } from '../left-menu/menu-components';
import { formatSoilFeatureName } from '../../utils/soils';

const ROW_HEIGHT = 38;
const ROW_MARGIN = 8;

interface LayerFeature extends Feature {
  id: string;
  properties: { id: string; name: string; rgbString: string };
}

const WrappedRow = styled.div`
  display: flex;
  align-self: start;
  line-height: 1.3;
  ${SquareSwatch} {
    margin-top: 3px;
  }
}
`;

const Row = ({ layerFeature }: { layerFeature: LayerFeature }) => {
  const {
    properties: { rgbString },
  } = layerFeature;
  return (
    <div style={{ marginBottom: ROW_MARGIN }}>
      <WrappedRow>
        <SquareSwatch
          borderColor={darkenColor(rgbString)}
          className="flex-shrink-0"
          color={rgbString}
        />
        <WrappedNameDiv className="ml-3">
          {formatSoilFeatureName(layerFeature)}
        </WrappedNameDiv>
      </WrappedRow>
    </div>
  );
};

export const useSoilLayerLegendInfo = () => {
  const {
    // @ts-expect-error MapContext not migrated to TS
    layers: { layers },
  } = useContext(MapContext);
  // @ts-expect-error FieldsPreviewContext not migrated to TS
  const { isPrintMode, panelVisibility, togglePanelVisibility } = useContext(
    FieldsPreviewContext,
  );

  const soilLayer = layers[SOIL_BOUNDARIES_LAYER_ID];
  const soilFeatures = useMemo(() => {
    if (!soilLayer || !layerHasData(soilLayer)) return [];

    const soilGroups = groupBy(soilLayer.data?.features, 'properties.name');
    return sortBy(
      Object.values(soilGroups).map(([firstFeature]) => firstFeature),
      'properties.name',
    );
  }, [soilLayer]);

  const rowsPerPanel = Math.floor(
    PANEL_INNER_HEIGHT / (ROW_HEIGHT + ROW_MARGIN),
  );
  const chunks = _.chunk(soilFeatures, rowsPerPanel);

  const header = (
    <SectionHeader
      title="Soil Legend"
      icon={<SoilIcon />}
      onClick={() => togglePanelVisibility('soilLegend')}
    />
  );

  // Do not display if no data in soil layer
  if (!soilFeatures.length && !soilLayer?.hasRemote) return [];

  if (!panelVisibility.soilLegend || !soilLayer.shown) {
    if (isPrintMode) return [];

    return [
      <PanelSection className="opacity-50" key="soil-legend-hidden">
        {header}
      </PanelSection>,
    ];
  }

  return chunks.map((chunk, i) => (
    <PanelSection
      // @ts-expect-error SidePanel not migrated to TS
      $hideBottomBorder={i !== chunks.length - 1}
      key={chunk.map(({ layerId }) => layerId).join('-')}
    >
      {i === 0 && header}

      {chunk.map((feature: LayerFeature) => (
        <Row key={feature.id || feature.properties.id} layerFeature={feature} />
      ))}
    </PanelSection>
  ));
};
